import React, { useMemo } from 'react';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import AlertBox from '@/components/layout/AlertBox';
import Mensagem from '../chat/Mensagem';
import { ChatMensagemDto } from '@tera/shared/src/dto/ChatMensagemDto';
import type { TranscriptionScript, CallSummary } from '@tera/shared/src/types/transcription';
import styles from './ModalTranscricao.module.css';
import Icon from '@/components/Icon';
import { mdiEmoticonHappy, mdiEmoticonNeutral, mdiEmoticonSad, mdiCheckCircle, mdiCloseCircle, mdiMinusCircle } from '@mdi/js';
import Grid, { GridItem } from '@/components/layout/Grid';

type Props = {
    show: boolean;
    onClose: () => void;
    chamadaId: number | null;
    transcricaoRoteiro?: TranscriptionScript;
    resumoChamada?: CallSummary;
};

const ModalTranscricao = ({ show, onClose, chamadaId, transcricaoRoteiro, resumoChamada }: Props) => {
    // Converter TranscriptionScriptEntry[] para ChatMensagemDto[]
    const mensagens = useMemo<ChatMensagemDto[]>(() => {
        if (!transcricaoRoteiro?.entries?.length) return [];

        return transcricaoRoteiro.entries.toReversed().map((entry, index) => ({
            id: index,
            data: entry.timestamp,
            data_criacao: entry.timestamp,
            nome: entry.speaker,
            tipo_mime: 'text/message',
            conteudo_ou_url: entry.text,
            origem: entry.speakerType === 'client' ? 'usuario' : (entry.speakerType === 'agent' ? 'atendente' : 'bot'),
            estado_atual: 'processada',
            metadados: {
                durationSec: entry.durationSec,
                confidence: entry.confidence,
            }
        } as ChatMensagemDto));
    }, [transcricaoRoteiro]);

    const sentimentIcon = useMemo(() => {
        if (!resumoChamada?.sentiment) return null;
        switch (resumoChamada.sentiment) {
            case 'positive':
                return { icon: mdiEmoticonHappy, color: 'var(--tc-color-success)', label: 'Positivo' };
            case 'negative':
                return { icon: mdiEmoticonSad, color: 'var(--tc-color-danger)', label: 'Negativo' };
            case 'neutral':
                return { icon: mdiEmoticonNeutral, color: 'var(--tc-color-gray-500)', label: 'Neutro' };
            default:
                return null;
        }
    }, [resumoChamada?.sentiment]);

    return (
        <Modal
            size="lg"
            title={`Transcrição da Ligação${chamadaId ? ` #${chamadaId}` : ''}`}
            show={show}
            onClose={onClose}
            hideControls
        >
            {resumoChamada && (
                <div className={styles.resumoContainer}>
                    {/* Resumo da chamada */}
                    <h3 className={styles.resumoTitulo}>Resumo da Chamada</h3>
                    <Grid style={{ fontSize: '0.8rem' }}>
                        <GridItem sm={12} md={6}>
                            {/* Sumário */}
                            {resumoChamada.summary && (
                                <div className={styles.resumoSecao}>
                                    <strong>Sumário:</strong>
                                    <p>{resumoChamada.summary}</p>
                                </div>
                            )}

                            {/* Outcome */}
                            {resumoChamada.outcome && (
                                <div className={styles.resumoSecao}>
                                    <strong>Resultado:</strong>
                                    <p>{resumoChamada.outcome}</p>
                                </div>
                            )}

                            {/* Sentimento */}
                            {sentimentIcon && (
                                <div className={styles.resumoSecao}>
                                    <strong>Sentimento:</strong>
                                    <HBox gap="8px" style={{ alignItems: 'center' }}>
                                        <Icon path={sentimentIcon.icon} size={1} color={sentimentIcon.color} />
                                        <span style={{ color: sentimentIcon.color, fontWeight: 500 }}>
                                            {sentimentIcon.label}
                                        </span>
                                    </HBox>
                                </div>
                            )}
                        </GridItem>
                        <GridItem sm={12} md={6}>
                            {/* Pontos-chave */}
                            {resumoChamada.keyPoints?.length > 0 && (
                                <div className={styles.resumoSecao}>
                                    <strong>Pontos-chave:</strong>
                                    <ul className={styles.resumoLista}>
                                        {resumoChamada.keyPoints.map((point, idx) => (
                                            <li key={idx}>{point}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </GridItem>
                    </Grid>
                </div>
            )}


            <VBox gap="16px" style={{ maxHeight: '80vh', overflow: 'hidden' }}>
                {/* Transcrição */}
                {mensagens.length > 0 ? (
                    <div className={styles.transcricaoContainer}>
                        <h3 className={styles.transcricaoTitulo}>Conversa Completa</h3>
                        <div className={styles.mensagensContainer}>
                            {mensagens.map((mensagem, index) => (
                                <Mensagem
                                    key={mensagem.id}
                                    item={mensagem}
                                    index={index}
                                    mensagens={mensagens}
                                    autor={mensagem.nome}
                                    onSelectedKbSection={() => { }}
                                />
                            ))}
                        </div>
                    </div>
                ) : (
                    <AlertBox variant="info">
                        Transcrição não disponível para esta ligação.
                    </AlertBox>
                )}
            </VBox>
        </Modal>
    );
};

export default ModalTranscricao;
